import { importsApis } from '@gleamer/apis';
import { Imports } from '@gleamer/types';
import { useSystem } from '@ohif/core';
import { useQuery } from '@tanstack/react-query';

const {
  fetchImportBatch,
  fetchImportBatchFiles,
  fetchImportBatchLines,
  fetchImportBatches,
  fetchImportSources,
} = importsApis;

type ImportBatchesQuery = Imports.ImportBatchesQuery;
type ImportBatchFilesQuery = Imports.ImportBatchFilesQuery;
type ImportBatchLinesQuery = Imports.ImportBatchLinesQuery;

/** While a batch is unfinished, the list and the batch are read again this often. */
export const IMPORTS_REFRESH_MS = 10_000;

/** The query keys a submitted batch invalidates. */
export const IMPORT_BATCHES_KEY = 'import-batches';
export const IMPORT_BATCH_KEY = 'import-batch';

/** The core singleton that carries the bearer token (see MyTasks), as the API functions take it. */
export function useAuthorization() {
  const { servicesManager } = useSystem();
  return servicesManager.services.userAuthenticationService;
}

export function useImportBatches(query: ImportBatchesQuery) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [IMPORT_BATCHES_KEY, query],
    queryFn: () => fetchImportBatches(auth, query),
    keepPreviousData: true,
    refetchInterval: data =>
      data?.content?.some(batch => !Imports.isFinalStatus(batch.status))
        ? IMPORTS_REFRESH_MS
        : false,
  });
}

export function useImportBatch(batchId: string) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [IMPORT_BATCH_KEY, batchId],
    queryFn: () => fetchImportBatch(auth, batchId),
    enabled: !!batchId,
    refetchInterval: data =>
      data && !Imports.isFinalStatus(data.status) ? IMPORTS_REFRESH_MS : false,
  });
}

/** A batch's lines; refreshed with the batch while it is unfinished (`live`). */
export function useImportBatchLines(
  batchId: string,
  query: ImportBatchLinesQuery,
  live: boolean
) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [IMPORT_BATCH_KEY, batchId, 'lines', query],
    queryFn: () => fetchImportBatchLines(auth, batchId, query),
    enabled: !!batchId,
    keepPreviousData: true,
    refetchInterval: live ? IMPORTS_REFRESH_MS : false,
  });
}

/** A batch's files — one line's, or those in some statuses; refreshed while the batch is unfinished. */
export function useImportBatchFiles(
  batchId: string,
  query: ImportBatchFilesQuery,
  { enabled = true, live }: { enabled?: boolean; live: boolean }
) {
  const auth = useAuthorization();
  return useQuery({
    queryKey: [IMPORT_BATCH_KEY, batchId, 'files', query],
    queryFn: () => fetchImportBatchFiles(auth, batchId, query),
    enabled: !!batchId && enabled,
    keepPreviousData: true,
    refetchInterval: live ? IMPORTS_REFRESH_MS : false,
  });
}

/** The buckets a list may be read from; they change only with viewer-api's configuration. */
export function useImportSources() {
  const auth = useAuthorization();
  return useQuery({
    queryKey: ['import-sources'],
    queryFn: () => fetchImportSources(auth),
    staleTime: Infinity,
  });
}
